import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { TagOutput } from '@1financial/shared';
import { useCapabilities } from '../hooks/useCapabilities.js';
import { useAuth, getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { httpError } from '../lib/http-error.js';

export type TaggableScope = 'CLIENT' | 'ENGAGEMENT' | 'COMPLIANCE_CLIENT' | 'COMPLIANCE_ITEM';

const SCOPE_FIELD: Record<TaggableScope, string> = {
  CLIENT: 'clientId',
  ENGAGEMENT: 'engagementId',
  COMPLIANCE_CLIENT: 'complianceClientId',
  COMPLIANCE_ITEM: 'complianceItemId',
};

/**
 * Tag chips + add/remove picker for one entity (client / engagement /
 * compliance client / compliance item). Reads GET /v1/tags/for/:scope/:id;
 * mutations go through /v1/tags/assign|unassign. Add/remove controls are
 * controlled by the effective tags.manage capability.
 */
export function EntityTags({ scope, entityId }: { scope: TaggableScope; entityId: string }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);
  const { canDo } = useCapabilities();
  const canEdit = canDo('tags.manage');

  const assigned = useQuery({
    queryKey: ['entity-tags', scope, entityId],
    queryFn: async () => {
      const res = await fetch(`/v1/tags/for/${scope}/${entityId}`, { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: TagOutput[] } };
      return body.data.items;
    },
  });

  const allTags = useQuery({
    queryKey: ['tags'],
    enabled: pickerOpen,
    queryFn: async () => {
      const res = await fetch('/v1/tags?limit=100', { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: TagOutput[] } };
      return body.data.items;
    },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['entity-tags', scope, entityId] });
    queryClient.invalidateQueries({ queryKey: ['tags'] });
  };

  const assign = useMutation({
    mutationFn: async (tagId: string) => {
      const res = await fetch('/v1/tags/assign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify({ tagId, scope, [SCOPE_FIELD[scope]]: entityId }),
      });
      if (!res.ok) throw await httpError(res);
    },
    onSuccess: invalidate,
  });

  const unassign = useMutation({
    mutationFn: async (tagId: string) => {
      const res = await fetch('/v1/tags/unassign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify({ tagId, scope, [SCOPE_FIELD[scope]]: entityId }),
      });
      if (!res.ok) throw await httpError(res);
    },
    onSuccess: invalidate,
  });

  const assignedIds = new Set((assigned.data ?? []).map((t) => t.id));
  const busy = assign.isPending || unassign.isPending;
  const mutationError = (assign.error?.message ?? unassign.error?.message) || null;

  if (assigned.error) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {(assigned.data ?? []).map((t) => (
        <span key={t.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs font-medium" style={{ borderColor: t.color, color: t.color, backgroundColor: `${t.color}1a` }}>
          {t.name}
          {canEdit && (
            <button
              onClick={() => unassign.mutate(t.id)}
              disabled={busy}
              className="hover:text-white disabled:opacity-50"
              title="Remove tag"
            >
              ✕
            </button>
          )}
        </span>
      ))}
      {assigned.isLoading && <span className="text-xs text-muted">…</span>}
      {!assigned.isLoading && (assigned.data ?? []).length === 0 && !canEdit && null}
      {canEdit && (
        <button
          onClick={() => setPickerOpen(true)}
          className="text-xs text-muted hover:text-accent border border-dashed border-border hover:border-accent px-2 py-0.5 rounded-md"
        >
          + Tag
        </button>
      )}

      {pickerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-bg-panel border border-border rounded-xl w-full max-w-sm mx-4 shadow-2xl animate-pop p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-white">Tags</h2>
              <button onClick={() => setPickerOpen(false)} className="text-muted hover:text-white">✕</button>
            </div>
            {allTags.isLoading && <p className="text-sm text-muted">Loading…</p>}
            {allTags.error && <p className="text-sm text-rose-400">{(allTags.error as Error).message}</p>}
            {allTags.data?.length === 0 && <p className="text-sm text-muted italic">No tags yet — create them on the Tags page.</p>}
            <ul className="space-y-1">
              {allTags.data?.map((t) => {
                const on = assignedIds.has(t.id);
                return (
                  <li key={t.id}>
                    <button
                      onClick={() => (on ? unassign.mutate(t.id) : assign.mutate(t.id))}
                      disabled={busy}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm hover:bg-bg-hover disabled:opacity-50"
                    >
                      <span className="inline-flex items-center gap-2 px-2 py-0.5 rounded-md border text-xs font-medium" style={{ borderColor: t.color, color: t.color, backgroundColor: `${t.color}1a` }}>
                        {t.name}
                      </span>
                      <span className={on ? 'text-accent text-xs' : 'text-muted text-xs'}>{on ? '✓ Assigned' : 'Assign'}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {mutationError && <p className="text-xs text-rose-400 mt-3">{mutationError}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
